'use client'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'

interface UsageLimitBannerProps {
  show: boolean
  resetTime: string // ISO 8601 timestamp
  onClose?: () => void
}

export default function UsageLimitBanner({
  show,
  resetTime,
  onClose,
}: UsageLimitBannerProps) {
  const { t } = useTranslation()
  const { colors } = useThemeMode()
  const [timeRemaining, setTimeRemaining] = useState('')

  useEffect(() => {
    if (!show || !resetTime) return

    const updateCountdown = () => {
      const now = new Date().getTime()
      const reset = new Date(resetTime).getTime()
      const distance = reset - now

      if (distance < 0) {
        setTimeRemaining(t('usageLimit.readyToChat'))
        if (onClose) {
          setTimeout(onClose, 2000) // Auto-close after 2 seconds
        }
        return
      }

      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((distance % (1000 * 60)) / 1000)

      setTimeRemaining(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      )
    }

    // Initial update
    updateCountdown()

    // Update every second
    const interval = setInterval(updateCountdown, 1000)
    return () => clearInterval(interval)
  }, [show, resetTime, onClose])

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '45px',
            backgroundColor: colors.windowBgAlpha,
            borderTop: `1px solid ${colors.primaryAccent}`,
            boxShadow: colors.isDark
              ? '0 -2px 12px rgba(0, 0, 0, 0.3)'
              : '0 -2px 12px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Background marquee with Honcho pixel */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              overflow: 'hidden',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                animation: 'marqueeSlowBounce 20s ease-in-out infinite',
                whiteSpace: 'nowrap',
              }}
            >
              {Array.from({ length: 30 }).map((_, i) => {
                const emojis = ['⏰', '💬', '⏳', '📨', '⌛', '💭', '🕐', '🔔']
                const emoji = emojis[i % emojis.length]
                return (
                  <span
                    key={i}
                    style={{
                      fontSize: '24px',
                      marginRight: '60px',
                      opacity: 0.4,
                      color: colors.visualAccent1,
                    }}
                  >
                    {emoji}
                  </span>
                )
              })}
            </div>
          </div>

          {/* Content */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '0 16px',
            }}
          >
            {/* Icon */}
            <svg
              width="20"
              height="18"
              viewBox="0 0 87 77"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                color: colors.primaryText,
              }}
            >
              <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
              <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
              <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
              <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
              <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
              <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
              <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
              <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
              <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
              <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
              <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
              <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
              <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
              <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
              <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
              <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
              <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
              <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
              <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
              <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
            </svg>

            {/* Message */}
            <p
              style={{
                margin: 0,
                fontSize: '13px',
                fontWeight: 500,
                color: colors.primaryText,
                lineHeight: 1,
              }}
            >
              {t('usageLimit.messageLimitReached')} <span style={{ color: colors.primaryAccent, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{timeRemaining}</span>
            </p>

            {/* Close button */}
            {onClose && (
              <button
                onClick={onClose}
                style={{
                  marginLeft: 'auto',
                  backgroundColor: 'transparent',
                  color: colors.secondaryText,
                  border: `1px solid ${colors.windowBorder}`,
                  borderRadius: '3px',
                  padding: '4px 10px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.windowBorder
                  e.currentTarget.style.color = colors.primaryText
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                  e.currentTarget.style.color = colors.secondaryText
                }}
              >
                {t('usageLimit.dismiss')}
              </button>
            )}
          </div>

          {/* Keyframe animations */}
          <style>
            {`
              @keyframes marqueeSlowBounce {
                0% {
                  transform: translateX(0);
                }
                50% {
                  transform: translateX(-20%);
                }
                100% {
                  transform: translateX(0);
                }
              }
            `}
          </style>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
